// The JDE EAC input folder, written to straight from the browser.
//
// Projection Sync writes FusionEAC.csv into
// \\thepatecompanies.com\jdefiles\JDE-<env>\EAC\InputFile, where the
// STR_ORCH_FusionEAC_BulkBatchUpload orchestrator picks it up. The folder is
// picked once per JDE environment with the File System Access API (Edge /
// Chrome) and the handle is kept in IndexedDB; the browser may still ask the
// user to re-allow write access in a new session. Writes use the user's own
// Windows access to the share.

const DB_NAME = 'fusion-jde-input-folder';
const STORE = 'handles';
const PROBE_FILE = '_fusion_write_check.tmp';

export const EAC_FILE_NAME = 'FusionEAC.csv';

// Folder name under \\thepatecompanies.com\jdefiles for each JDE environment.
export const JDE_SHARE_FOLDER = { prod: 'JDE-PD', test: 'JDE-PY', dev: 'JDE-DV' };

export const inputFolderPath = (env) =>
    `\\\\thepatecompanies.com\\jdefiles\\${JDE_SHARE_FOLDER[env] || JDE_SHARE_FOLDER.prod}\\EAC\\InputFile`;

export const folderWriteSupported = () =>
    typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

const openDb = () => new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
});

const withStore = async (mode, fn) => {
    const db = await openDb();
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, mode);
            const req = fn(tx.objectStore(STORE));
            tx.oncomplete = () => resolve(req?.result);
            tx.onerror = () => reject(tx.error);
        });
    } finally {
        db.close();
    }
};

export const getSavedFolder = async (env) => {
    try {
        return (await withStore('readonly', (s) => s.get(env))) || null;
    } catch {
        return null;
    }
};

const saveFolder = (env, handle) => withStore('readwrite', (s) => s.put(handle, env));

export const forgetFolder = (env) => withStore('readwrite', (s) => s.delete(env));

// Read + write permission on the folder. Must run inside a click handler the
// first time in a session, since the browser may show a prompt.
export const ensureWriteAccess = async (handle) => {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    return (await handle.requestPermission(opts)) === 'granted';
};

// The picked folder has to look like the orchestrator's input folder and take a write.
const checkFolder = async (handle) => {
    if (handle.name !== 'InputFile') {
        throw new Error(`Picked "${handle.name}". Pick the InputFile folder itself.`);
    }
    try {
        await handle.getDirectoryHandle('Archive');
    } catch {
        throw new Error('That InputFile folder has no Archive folder, so it isn\'t the JDE EAC input folder.');
    }
    const probe = await handle.getFileHandle(PROBE_FILE, { create: true });
    const w = await probe.createWritable();
    await w.write('ok');
    await w.close();
    await handle.removeEntry(PROBE_FILE);
};

export const pickFolder = async (env) => {
    const handle = await window.showDirectoryPicker({ id: `jde-eac-${env}`, mode: 'readwrite' });
    await checkFolder(handle);
    await saveFolder(env, handle);
    return handle;
};

// Size in bytes of an existing file in the folder, or null when there isn't one.
export const existingFileSize = async (handle, name) => {
    try {
        return (await (await handle.getFileHandle(name)).getFile()).size;
    } catch {
        return null;
    }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The browser writes to a swap file and moves it into place on close(). On the
// JDE share that close() has failed with InvalidStateError ("state had changed
// since it was read from disk"), most likely because the share updates file
// timestamps a moment late. So: retry with a pause, confirm the size that landed,
// and never leave an empty or partial file for the orchestrator to pick up.
export const writeTextFile = async (handle, name, text, { attempts = 3, onRetry } = {}) => {
    const bytes = new Blob([text]).size;
    let lastErr = null;
    for (let attempt = 1; attempt <= attempts; attempt++) {
        let step = 'open';
        try {
            const file = await handle.getFileHandle(name, { create: true });
            step = 'createWritable';
            const w = await file.createWritable();
            step = 'write';
            await w.write(text);
            step = 'close';
            await w.close();
            step = 'check size';
            const landed = (await file.getFile()).size;
            if (landed !== bytes) throw new Error(`${landed} bytes on the share, expected ${bytes}`);
            return bytes;
        } catch (err) {
            lastErr = new Error(`${step} failed on try ${attempt} of ${attempts}: ${err?.name && err.name !== 'Error' ? `${err.name}: ` : ''}${err?.message || err}`);
            if (attempt < attempts) {
                onRetry?.(lastErr);
                await sleep(1500 * attempt);
            }
        }
    }
    try {
        await handle.removeEntry(name);
    } catch {
        // nothing left behind
    }
    throw lastErr;
};
